import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCmsContent } from '../../redux/manageCms/actionCreator';

/**
 * Module-level in-memory cache for CMS content.
 * Survives re-renders; resets on full page reload.
 * Key: CMS key string   →   Value: resolved CMS value string
 *
 * fetchCmsContent has NO built-in caching (confirmed during Phase 0 exploration).
 * This cache prevents duplicate HTTP requests when multiple components on the
 * same screen call useCMS with overlapping keys.
 */
const cmsCache = {};

/**
 * Tracks in-flight requests to prevent duplicate concurrent fetches for the
 * same key (de-duplication).
 * Key: batch cache key   →   Value: Promise
 */
const pendingRequests = {};

// ---------------------------------------------------------------------------
// Global CMS loading observable
// Counts how many distinct CMS batches are currently in-flight.
// Subscribers (e.g. OnboardingLayout) receive a boolean whenever it changes
// so they can show/hide a loading overlay without prop-drilling or context.
// ---------------------------------------------------------------------------
let cmsActiveCount = 0;
const cmsLoadingListeners = new Set();

/**
 * Subscribe to global CMS loading state.
 * The listener is called immediately with the current state on subscription,
 * then again whenever the count transitions between 0 and >0.
 * Returns an unsubscribe function.
 */
export const subscribeCmsLoading = (listener) => {
  cmsLoadingListeners.add(listener);
  listener(cmsActiveCount > 0);
  return () => cmsLoadingListeners.delete(listener);
};

const updateCmsActive = (delta) => {
  cmsActiveCount += delta;
  const isLoading = cmsActiveCount > 0;
  cmsLoadingListeners.forEach((fn) => fn(isLoading));
};

/**
 * useCMS
 * Fetch CMS content for a given array of keys, with module-level caching
 * and in-flight request deduplication.
 *
 * Usage:
 *   const { get, loading } = useCMS(['onboarding_welcome_title', 'onboarding_welcome_subtitle']);
 *   const title = get('onboarding_welcome_title', 'Welcome!');
 *
 * Parameters:
 *   keys     — array of CMS key strings to fetch
 *
 * Returns:
 *   { get(key, fallback): string, loading: boolean }
 */
const useCMS = (keys = []) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [, forceRender] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!keys || keys.length === 0) return;

    // Determine which keys are not yet cached
    const uncachedKeys = keys.filter((k) => !(k in cmsCache));
    if (uncachedKeys.length === 0) return; // All keys already cached

    // Build a stable batch cache key from sorted uncached keys
    const batchKey = [...uncachedKeys].sort().join('|');

    // If an identical batch is already in-flight, wait for it instead of re-fetching
    if (pendingRequests[batchKey]) {
      pendingRequests[batchKey].then(() => {
        if (mountedRef.current) forceRender((n) => n + 1);
      });
      return;
    }

    setLoading(true);
    updateCmsActive(+1);

    const promise = dispatch(fetchCmsContent({ keys: uncachedKeys }))
      .then((result) => {
        // fetchCmsContent returns the ApiResponse envelope: { status, message, result: [{key, value}, ...] }
        // Unwrap result.result to reach the actual [{key, value}] array.
        const payload = (result && Array.isArray(result.result)) ? result.result : result;
        if (payload && typeof payload === 'object') {
          const items = Array.isArray(payload) ? payload : Object.entries(payload).map(([k, v]) => ({ key: k, value: v }));
          items.forEach((item) => {
            if (item && item.key) {
              cmsCache[item.key] = item.value ?? '';
            }
          });
        }
        // Mark any still-uncached keys as empty string so we don't re-fetch
        uncachedKeys.forEach((k) => {
          if (!(k in cmsCache)) cmsCache[k] = '';
        });
      })
      .catch(() => {
        // On error, populate with empty strings to prevent infinite re-fetch loops
        uncachedKeys.forEach((k) => {
          if (!(k in cmsCache)) cmsCache[k] = '';
        });
      })
      .finally(() => {
        delete pendingRequests[batchKey];
        updateCmsActive(-1); // always decrement regardless of mount state
        if (mountedRef.current) {
          setLoading(false);
          forceRender((n) => n + 1);
        }
      });

    pendingRequests[batchKey] = promise;
  }, [keys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * get(key, fallback)
   * Returns the cached CMS value for the given key, or fallback if not yet
   * loaded or not found.
   */
  const get = (key, fallback = '') => {
    if (key in cmsCache && cmsCache[key] !== '') return cmsCache[key];
    return fallback;
  };

  const getHtml = (key, fallback = '') => {
    const raw = get(key, fallback).trim();
    // If the entire value is a single <p>...</p> wrapper, strip it.
    // This prevents invalid nesting when the value lands inside <h1>, <h2>, <button>, etc.
    // Multi-paragraph values (<p>...</p><p>...</p>) are left untouched.
    const unwrapped = /^<p\b[^>]*>([\s\S]*?)<\/p>$/i.test(raw)
      ? raw.replace(/^<p\b[^>]*>([\s\S]*?)<\/p>$/i, '$1').trim()
      : raw;
    // Add mb-0 to any remaining <p> tags so they don't add unwanted margin.
    const html = unwrapped.replace(/<p\b([^>]*)>/g, (match, attrs) => {
      if (/class="([^"]*)"/.test(attrs)) {
        return match.replace(/class="([^"]*)"/, 'class="$1 mb-0"');
      }
      return `<p${attrs} class="mb-0">`;
    });
    return { __html: html };
  };

  return { get, getHtml, loading };
};

export default useCMS;
